import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Heart, UsersFour, ShieldCheck, Handshake,
  CheckCircle, Sparkle, Users, Certificate, Target, Brain
} from "@phosphor-icons/react";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: Heart,
      title: "Personalized Treatment",
      description: "We recognize that every organization and care team faces unique challenges. That's why we take the time to understand your specific goals, assess your operational needs, and deliver tailored strategies that truly fit—not just standard solutions.",
      gradient: "from-rose-500 to-pink-600"
    },
    {
      icon: ShieldCheck,
      title: "Building Confidence",
      description: "We believe comfort isn't just physical—it's about feeling supported, understood, and confident in the guidance you receive. From our first consultation to ongoing support, we create a professional, welcoming experience that puts your organization's goals and peace of mind first.",
      gradient: "from-cyan-500 to-blue-600"
    },
    {
      icon: Certificate,
      title: "Confidentiality",
      description: "We treat every client relationship with the highest level of confidentiality and professionalism. All organizational, operational, and clinical information shared with us is handled securely and in full compliance with industry standards and regulations.",
      gradient: "from-indigo-500 to-purple-600"
    },
    {
      icon: UsersFour,
      title: "Exceptional Leaders",
      description: "Through our strong network of healthcare professionals, we offer organizations access to diverse expertise across leadership, clinical operations, compliance, and team development. This collaborative approach allows us to deliver well-rounded, strategic support.",
      gradient: "from-emerald-500 to-teal-600"
    }
  ];

  return (
    <div data-testid="about-page" className="overflow-hidden">
      {/* Hero Section - Futuristic */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="about-hero">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="badge badge-accent mb-8">
                <Brain weight="fill" className="w-4 h-4 mr-2" />
                About Noble Hospice Solutions
              </span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
                Real Solutions for{" "}
                <span className="text-gradient">Healthcare Excellence</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10">
                We are dedicated to supporting your hospice's growth and the well-being 
                of those you serve. Led by experts with decades of combined experience 
                in hospice, compliance, and healthcare operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact" 
                  className="btn-accent inline-flex items-center justify-center gap-2" 
                  data-testid="about-cta"
                >
                  Book Free Consultation
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 rounded-3xl blur-lg opacity-30" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758691462848-ba1e929da259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVjaG5vbG9neSUyMGRpZ2l0YWwlMjBpbm5vdmF0aW9ufGVufDB8fHx8MTc3MzAzMTQ2Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Medical stethoscope - healthcare excellence"
                  className="w-full aspect-[4/3] object-cover"
                  data-testid="about-hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
              </div>
              
              {/* Floating Card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 glass-card p-5 rounded-2xl shadow-xl"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <ShieldCheck weight="fill" className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-slate-900">HIPAA Compliant</p>
                    <p className="text-sm text-slate-500">CMS CoP & CHAP Aligned</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="badge badge-accent mb-6">Our Mission</span>
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-8 leading-tight tracking-tight">
                We support healthcare organizations with practical, real-world solutions
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                At Noble Hospice Solutions, we provide QAPI guidance, enhance IDG processes, 
                onboarding tools, staff training, and growth strategies tailored to your hospice needs.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We also offer executive mentorship, interim leadership, and compliance support 
                to help you lead with confidence and stay survey-ready. Whether you're building 
                structure, navigating change, or aiming to grow, we're here to help you do it 
                the right way—with clarity, care, and lasting results.
              </p>
            </motion.div>

            <motion.div
              className="mt-12 relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-3xl blur-xl" />
              <div className="relative p-10 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-2xl border border-cyan-500/20">
                <p className="text-xl md:text-2xl text-slate-700 font-medium italic font-display">
                  "Through compliance, collaboration and innovation, we aim to transform your care 
                  to creating lasting impacts for your patients one at a time while strengthening 
                  your workforce with lasting experiences."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Advantage Section - Futuristic Cards */}
      <section className="section-padding bg-slate-50" data-testid="advantage-section">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Why Choose Us</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Our Competitive Advantage
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              What sets Noble Hospice Solutions apart from other consulting firms
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 border border-slate-100 hover:border-cyan-500/30 hover:shadow-2xl transition-all duration-500 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`advantage-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${advantage.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <advantage.icon weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-4">{advantage.title}</h3>
                <p className="text-slate-600 leading-relaxed">{advantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A New Standard Section */}
      <section className="section-padding bg-white" data-testid="standard-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-3xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1603839834958-ae04ebbb3ec1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxob3NwaWNlJTIwY2FyZSUyMGNvbXBhc3Npb24lMjBlbGRlcmx5fGVufDB8fHx8MTc3MzAzMTQ2NHww&ixlib=rb-4.1.0&q=85"
                alt="Caring hands in healthcare"
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-accent mb-6">Guided by Purpose</span>
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-8 tracking-tight">
                A New Standard in Hospice Consulting
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                At Noble Hospice Solutions, we are redefining what excellence looks 
                like in hospice care leadership and compliance. Our consultants bring 
                decades of real-world experience to help your team navigate regulatory 
                demands, strengthen operations, and improve patient outcomes.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                Whether you're growing, stabilizing, or rebuilding, we work with you 
                to elevate every aspect of your organization with precision, compassion, 
                and proven strategies.
              </p>
              
              <div className="space-y-5">
                {[
                  "Targeted audits addressing patient care and clinical documentation",
                  "Certified Project Management team for clinical and non-clinical projects",
                  "Reduce billing denials and improve survey preparedness",
                  "Strengthen your workforce with lasting experiences"
                ].map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle weight="fill" className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Futuristic */}
      <section className="cta-section section-padding" data-testid="about-cta-section">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-cyan-500/20 rounded-full" />
        </div>
        
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-8">
              <Sparkle weight="fill" className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Secure Your Exclusive Consultation Today
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
              Your journey to better care starts here. Explore our services and take 
              the first step with a team that understands your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-1 inline-flex items-center justify-center gap-2"
                data-testid="cta-contact"
              >
                Book Free Consultation
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-slate-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-slate-800 hover:border-slate-500 inline-flex items-center justify-center gap-2"
                data-testid="cta-services"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
